import { LucideIcon, ArrowDown, ArrowUp, Minus } from 'lucide-react';

export type KpiTone = 'neutral' | 'healthy' | 'attention' | 'critical';
export type TrendDirection = 'up' | 'down' | 'flat';

interface KpiCardProps {
  label: string;
  value: string | number;
  icon: LucideIcon;
  trend?: string;
  trendDirection?: TrendDirection;
  /** good = up is good (green up / red down); bad = up is bad (red up / green down) */
  trendPolarity?: 'good' | 'bad';
  tone?: KpiTone;
  color?: string;
}

const toneStyles: Record<KpiTone, { wrap: string; icon: string }> = {
  neutral:   { wrap: 'border-border',                       icon: 'bg-primary/10 text-primary' },
  healthy:   { wrap: 'border-emerald-500/30 shadow-[0_0_0_1px_hsl(142_70%_45%/0.15)]', icon: 'bg-emerald-500/10 text-emerald-400' },
  attention: { wrap: 'border-yellow-500/40 shadow-[0_0_24px_-12px_hsl(45_100%_55%/0.55)]', icon: 'bg-yellow-500/10 text-yellow-400' },
  critical:  { wrap: 'border-red-500/50 shadow-[0_0_28px_-10px_hsl(0_85%_55%/0.55)]', icon: 'bg-red-500/15 text-red-400' },
};

export function KpiCard({
  label, value, icon: Icon, trend, trendDirection, trendPolarity = 'good', tone = 'neutral', color,
}: KpiCardProps) {
  const t = toneStyles[tone];
  const TrendIcon = trendDirection === 'up' ? ArrowUp : trendDirection === 'down' ? ArrowDown : trendDirection === 'flat' ? Minus : null;
  const trendColor = !trendDirection || trendDirection === 'flat'
    ? 'text-muted-foreground'
    : (trendDirection === 'up') === (trendPolarity === 'good')
      ? 'text-emerald-400'
      : 'text-red-400';
  return (
    <div className={`bg-card border rounded-lg p-4 flex items-start gap-4 transition-colors ${t.wrap}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${color ?? t.icon}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold">{value}</p>
        {trend && (
          <p className={`text-xs mt-0.5 flex items-center gap-1 ${trendColor}`}>
            {TrendIcon && <TrendIcon className="h-3 w-3" />}
            {trend}
          </p>
        )}
      </div>
    </div>
  );
}
